import React, { useState } from "react";
import dayjs from "dayjs";
import Link from "next/link";
import { InView } from "react-intersection-observer";
import { useGQLClient } from "contexts/appContext";
import { canApplyForInterview, eventTimeConstructor } from "common";
import { Icon } from "components/atoms/icon";
import { Card } from "components/atoms/card";
import { getCompanyShowPath, getEventPath } from "common";
import Loader from "react-loaders";
import { sendGAEvent } from "lib/analytics";
import { isBrowser } from "lib/env";
import { encode } from "querystring";

import { wrap } from "../../lib/hoc";
import { PageLayout } from "../../components/molecules/pageLayout";
import { Routes } from "../../generated/routes";
import {
  ClientStatus,
  EventDetailQuery,
  EventStatus,
  EventType,
} from "../../generated/graphql";
import { Breadcrumbs } from "../../components/molecules/breadcrumb";
import { Headline } from "../../components/atoms/headline";
import styles from "./PageEventDetail.module.scss";
import EventDescription from "./EventDescription";
import ImageWithLoader from "../../components/atoms/imageWithLoader";
import eventCoverPlaceholderBig from "../../../assets/images/coverPlaceholderBig.jpg";
import {
  ContentTwoColumn,
  ContentTwoColumnLeft,
  ContentTwoColumnRight,
} from "../../components/atoms/contentTwoColumn";
import { InterpretedText } from "../../components/atoms/interpretedText";
import { CompanyGrid } from "../../components/atoms/companyGrid";
import EventRightContent from "./EventRightContent";
import EventContent from "./EventContent";
import { imageSizerString, mapURL } from "../../lib/fun";
import { DropDown, withNullOption } from "../../components/molecules/dropDown";
import { TabBookmark, TabBookmarkGroup } from "../../components/atoms/tab";
import EventDateOptionCard from "./EventDateOptionCard";
import { AddressBlock } from "../../components/molecules/addressBlock";
import { doorsOpenAtLabel } from "../register/common";
import { CompanyCard } from "../../components/molecules/companyCard";
import { usePageEventDetail } from "./PageEventDetailHook";
import { CurrentEntor, EventInstanceItem } from "../../types";
import { EventCard } from "../../components/molecules/eventCard";
import { EventGrid } from "../../components/atoms/eventGrid";
import sponsoredEventSvg from "../../../assets/images/sponsored-event.svg";
import seminarsSvg from "../../../assets/images/seminars.svg";
import interviewsSvg from "../../../assets/images/interview.svg";
import internshipSvg from "../../../assets/images/internship.svg";
import mentorEventsSvg from "../../../assets/images/mentor-events.svg";
import finalSelectionSvg from "../../../assets/images/final-selection.svg";

const INTERVIEW_EVENT_HASH = "k4Comco5Cl3ogUMxNBHf";

export interface EventDetailPageProps {
  event: EventDetailQuery["event"];
  venue?: number;
  currentEntor?: CurrentEntor;
  canApplyForInt?: boolean;
  isInterviewSignupEvent?: boolean;
  baklavaDisableLegacyEventWrites?: boolean;
}

const PageEventDetail: React.FC<EventDetailPageProps> = (props) => {
  const {
    event,
    eventIsFull,
    device,
    setHeadlineRef,
    headlines,
    goToHeadline,
    activeHeadline,
    onScroll,
    hasRegisteredVenue,
    upcomingDates,
    eventDetailForm,
    upcomingPrefectures,
    selectedDate,
    setSelectedDate,
    eventRegistrationStatus,
    cancelEvent,
    registrationURL,
    initialSelectedPrefecture,
    goToJobhuntInfoEdit,
  } = usePageEventDetail(props);

  const {
    formValues: { selectedPrefecture },
    field,
  } = eventDetailForm;
  const { gqlClient } = useGQLClient();
  const [relatedEvents, setRelatedEvents] = useState<EventInstanceItem[]>([]);
  const [relatedEventsLoaded, setRelatedEventsLoaded] = useState<boolean>(
    false,
  );

  return (
    <PageLayout
      onScroll={onScroll}
      fixedButtonBelow
      title={event.title}
      metaImage={event.imageUrl || undefined}
      noIndex={event.status === EventStatus.NoIndex}
    >
      <div className={styles.header}>
        <div className={styles.hideBreadcrumbsOnMobile}>
          <Breadcrumbs
            breadcrumbs={[
              {
                label: "検索結果",
                to: Routes.url("/events/page/[page]", { page: 1 }),
              },
              { label: event.title || "ロード中" },
            ]}
          />
        </div>
        <EventDescription
          e2e="event-detail-event-title"
          event={event}
          lotteryRejected={event.lotteryEventApplicationStatus === "rejected"}
          eventIsFull={eventIsFull}
          prefectureNames={upcomingPrefectures.map((p) => p.name)}
        />
        <div className={styles.imageWrapper}>
          <div className={styles.imageWrapperInner}>
            <ImageWithLoader
              className={styles.image}
              fallback={eventCoverPlaceholderBig}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              src={`${imageSizerString(event.imageUrl!, 1168, 584)}`}
            />
          </div>
        </div>
      </div>
      <ContentTwoColumn>
        <ContentTwoColumnLeft>
          {props.baklavaDisableLegacyEventWrites ? (
            <div className={styles.maintenanceNotification}>
              現在、メンテナンス中のため、申し込みは受け付けておりません。
            </div>
          ) : null}
          {event.scheduledEvent &&
          event.lotteryEventApplicationStatus !== "rejected" ? (
            <div className={styles.registrationSectionWrapper}>
              <div className={styles.reminder}>
                {hasRegisteredVenue &&
                  "このイベントにはすでに申し込んでいます。他の日程に申し込む場合には、一度、申し込みをキャンセルしてください。"}
              </div>
              <div className={styles.noVenueText}>
                {upcomingDates.length === 0 && (
                  <div>開催予定のイベントがありません。</div>
                )}
              </div>
              <div className={styles.areaTabs}>
                <div className={styles.mobileTab}>
                  <DropDown
                    type="medium"
                    {...field("selectedPrefecture")}
                    {...withNullOption({
                      options: upcomingPrefectures,
                      renderLabel: (p) => p.name,
                      toStr: (p) => p.name,
                      fromStr: (str) =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        upcomingPrefectures.find((p) => p.name === str)!,
                    })}
                  />
                </div>
                <div className={styles.desktopTab}>
                  <TabBookmarkGroup>
                    {upcomingPrefectures.map((p) => (
                      <TabBookmark
                        key={p.id}
                        isActive={p.id === selectedPrefecture?.id}
                        label={p.name}
                        onSelect={() => field("selectedPrefecture").setValue(p)}
                        autoScrollTo={Boolean(
                          initialSelectedPrefecture &&
                            initialSelectedPrefecture.id === p.id,
                        )}
                      />
                    ))}
                  </TabBookmarkGroup>
                </div>
              </div>

              <div className={styles.venueSectionWrapper}>
                <div className={styles.venueItemsContainer}>
                  <div className={styles.region}>
                    <div className={styles.venues}>
                      {upcomingDates
                        .filter(
                          (date) =>
                            date.place.prefecture.id === selectedPrefecture?.id,
                        )
                        .map((venue) => (
                          <EventDateOptionCard
                            availableSeats={
                              venue.reservationNum - venue.numApplied > 0
                                ? venue.reservationNum - venue.numApplied
                                : 0
                            }
                            isRegisteredVenue={
                              venue.currentEntorApplied || false
                            }
                            endDate={venue.endAt}
                            key={`venue-${venue.id}`}
                            locationLink={
                              venue.place.prefecture.id === 49
                                ? ""
                                : mapURL(venue.place)
                            }
                            selected={
                              selectedDate
                                ? selectedDate.id === venue.id
                                : false
                            }
                            startDate={venue.startAt}
                            status={
                              venue.reservationNum - venue.numApplied > 0
                                ? "free"
                                : "full"
                            }
                            takenSeats={venue.numApplied}
                            onClick={() => {
                              if (isBrowser) {
                                sendGAEvent({
                                  event: "enc.time_slot_select",
                                  screen_name: "event_detail",
                                  event_hash: event.hash,
                                });
                              }
                              setSelectedDate(venue);
                            }}
                            doorsOpenAt={venue.doorsOpenAt || ""}
                          />
                        ))}
                    </div>
                  </div>{" "}
                </div>
              </div>
            </div>
          ) : null}
          <EventContent
            setHeadlineRef={setHeadlineRef}
            activeHeadline={activeHeadline}
            headlines={headlines}
            event={event}
            goToHeadline={goToHeadline}
            isMobile={device === "mobile"}
          />
          {event.scheduledEvent && (
            <div>
              <div className={styles.eventInfoHeadline}>開催概要</div>
              <div className={styles.eventInfoLabel}>服装</div>
              <div className={styles.eventInfo}>
                {event.scheduledEvent.dressCode}
              </div>
              <div className={styles.eventInfoLabel}>持ち物</div>
              <div className={styles.eventInfo}>
                {event.scheduledEvent.belongings}
              </div>
              <div className={styles.eventInfoLabel}>参加費用</div>
              <div className={styles.eventInfo}>{event.scheduledEvent.fee}</div>
              <div className={styles.eventInfoLabel}>お問い合わせ</div>
              <div className={styles.eventInfo}>
                <InterpretedText text={event.scheduledEvent.contact || ""} />
              </div>
              <div className={styles.line} />
              {selectedDate && (
                <div className={styles.eventInfoHeadline}>開催拠点</div>
              )}
              {selectedDate && (
                <AddressBlock
                  key={selectedDate.id}
                  addressData={selectedDate.place}
                  doorsOpenAt={doorsOpenAtLabel(selectedDate.doorsOpenAt || "")}
                  eventTime={eventTimeConstructor(selectedDate)}
                />
              )}
            </div>
          )}
          <ListCompanies event={event} />
          <ExploreEvents />
          <InView
            as="div"
            onChange={async (inView) => {
              if (inView && !relatedEventsLoaded) {
                const {
                  relatedEventInstances,
                } = await gqlClient.RelatedEventInstances({
                  filter: {
                    targetYears: props.currentEntor
                      ? [props.currentEntor.user.lastGraduationYear]
                      : null,
                    limit: 6,
                    deadline: dayjs().format(),
                    hash: event.hash,
                  },
                });
                setRelatedEventsLoaded(true);
                setRelatedEvents(relatedEventInstances);
              }
            }}
          >
            {relatedEvents.length === 0 && !relatedEventsLoaded ? (
              <Loader active type="ball-scale-multiple" />
            ) : relatedEvents.length > 0 ? (
              <div className={styles.sectionSpacer}>
                <div className={styles.inlineContainer}>
                  <Headline
                    className={styles.headline}
                    main="RELATED EVENTS"
                    sub="関連イベント"
                  />
                  <div className={styles.viewAllLinkContainer}>
                    <Link href={Routes.url("/events/page/[page]", { page: 1 })}>
                      <a>View all</a>
                    </Link>
                  </div>
                </div>
                <div className={styles.relatedEventsContainer}>
                  <EventGrid smallCard={true}>
                    {relatedEvents.map((eventInstance) => (
                      <Link
                        key={`${eventInstance.instanceType}${eventInstance.id}`}
                        href={Routes.url(
                          "/events/[hash]",
                          eventInstance.instanceType === "ScheduledEventDate"
                            ? {
                                hash: eventInstance.scheduledEvent.event.hash,
                                venue: eventInstance.id,
                              }
                            : {
                                hash: eventInstance.event.hash,
                              },
                        )}
                      >
                        <a data-e2e={"related-event-card"}>
                          <EventCard
                            mode="small"
                            eventInstance={eventInstance}
                          />
                        </a>
                      </Link>
                    ))}
                  </EventGrid>
                </div>
              </div>
            ) : null}
          </InView>
        </ContentTwoColumnLeft>
        <ContentTwoColumnRight>
          <EventRightContent
            goToJobhuntInfoEdit={goToJobhuntInfoEdit}
            status={eventRegistrationStatus}
            eventIsFull={eventIsFull}
            registrationButtonURL={registrationURL}
            activeHeadline={activeHeadline}
            goToHeadline={goToHeadline}
            headlines={headlines}
            cancelEvent={cancelEvent}
            eventType={event.entryEvent ? EventType.Entry : EventType.Scheduled}
            lotteryRejected={event.lotteryEventApplicationStatus === "rejected"}
            lotteryEvent={event.isLotteryEvent}
            isInterviewSignupEvent={props.isInterviewSignupEvent}
            canApplyForInt={!!props.canApplyForInt}
            currentEntor={props.currentEntor}
            baklavaDisableLegacyEventWrites={
              props.baklavaDisableLegacyEventWrites
            }
          />
        </ContentTwoColumnRight>
      </ContentTwoColumn>
    </PageLayout>
  );
};

const ExploreEvents: React.FC = () => {
  const exploreEvents = [
    {
      title: "インターン",
      svg: internshipSvg,
      className: styles.internship,
      categoryId: 5,
    },
    {
      title: "en-courage主催\nイベント",
      svg: sponsoredEventSvg,
      className: styles.sponsored,
      categoryId: 1,
    },
    {
      title: "本選考",
      svg: finalSelectionSvg,
      className: styles.finalSelection,
      categoryId: 3,
    },
    {
      title: "説明会・セミナー",
      svg: seminarsSvg,
      className: styles.seminar,
      categoryId: 4,
    },
    {
      title: "メンター主催\nイベント",
      svg: mentorEventsSvg,
      className: styles.mentor,
      categoryId: 2,
    },
    {
      title: " 会食・面談",
      svg: interviewsSvg,
      className: styles.interview,
      categoryId: 6,
    },
  ];

  return (
    <div className={styles.sectionSpacer}>
      <div className={styles.inlineContainer}>
        <Headline
          className={styles.headline}
          main="EXPLORE EVENTS"
          sub="イベントを探索する"
        />
        <div className={styles.viewAllLinkContainer}>
          <Link href={Routes.url("/events/page/[page]", { page: 1 })}>
            <a>View all</a>
          </Link>
        </div>
      </div>
      <div className={styles.exploreEventsContainer}>
        {exploreEvents.map(({ title, svg, className, categoryId }) => (
          <ExploreEventCard
            key={title}
            title={title}
            svg={svg}
            className={className}
            categoryId={categoryId}
          />
        ))}
      </div>
    </div>
  );
};
interface ExploreEventCardProps {
  title: string;
  className: string;
  svg: string;
  categoryId: number;
}
const ExploreEventCard = ({
  title,
  className,
  svg,
  categoryId,
}: ExploreEventCardProps) => {
  return (
    <Link href={`/events/page/1?categoryIds=${categoryId}`}>
      <a>
        <Card smallCard={true}>
          <div className={[styles.exploreEventsCard, className].join(" ")}>
            <img src={svg} />
            <div>
              <pre>{title}</pre>
            </div>
          </div>
        </Card>
      </a>
    </Link>
  );
};

interface ListCompaniesProps {
  event: EventDetailQuery["event"];
}
const ListCompanies: React.FC<ListCompaniesProps> = ({ event }) => {
  const [showAll, setShowAll] = React.useState<boolean>(false);
  if (!event.clients.length) return null;
  const visibleClients = event.clients.filter(
    ({ status }) => status === ClientStatus.Visible,
  );
  if (!visibleClients.length) return null;

  let renderClients = visibleClients;
  if (visibleClients.length > 6) {
    if (!showAll) renderClients = visibleClients.slice(0, 6);
  }

  return (
    <div className={styles.sectionSpacer}>
      <div className={styles.eventInfoHeadline}>開催企業</div>
      <CompanyGrid>
        {renderClients.map((client) => (
          <Link key={client.id} href={getCompanyShowPath(client.id)}>
            <a {...{ "data-e2e": "company-list-company-card" }}>
              <CompanyCard client={client} />
            </a>
          </Link>
        ))}
      </CompanyGrid>
      {visibleClients.length > 6 && (
        <div className={styles.showMoreClients}>
          <span onClick={() => setShowAll((value) => !value)}>
            {!showAll ? (
              <>
                <Icon name="chevron_down" />
                すべて表示 ({visibleClients.length + 1})
              </>
            ) : (
              <>
                <Icon name="chevron_up" />
                閉じる
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

const redirectEvents: Record<string, string> = {
  "9adce5804691621cbe5c0291d6c3d6c5e58b41a7bfe82ced0acf8283a76fb25f":
    "https://npo.en-courage.com/interview-application",
};

export default wrap(PageEventDetail, async ({ gqlClient, context }) => {
  const { currentEntor } = context;
  const { hash, venue } = Routes.getParamsMust("/events/[hash]", context.query);

  if (context.baklavaFeatureFlags?.baklavaPublicEvents) {
    const { hash: _hash, ...query } = context.query;
    return {
      status: "redirectTo",
      path: getEventPath(hash, encode(query)),
    };
  }

  if (redirectEvents[hash]) {
    return {
      status: "redirectTo",
      path: redirectEvents[hash],
    };
  }

  const res = await gqlClient.EventDetail({ hash });

  const canApplyForInt =
    !currentEntor || (currentEntor && canApplyForInterview(currentEntor));
  const isInterviewSignupEvent = res.event.hash === INTERVIEW_EVENT_HASH;

  return {
    status: "ok",
    pageProps: {
      event: res.event,
      venue,
      currentEntor: context.currentEntor,
      canApplyForInt,
      isInterviewSignupEvent,
      baklavaDisableLegacyEventWrites:
        context.baklavaFeatureFlags?.baklavaDisableLegacyEventWrites,
    },
  };
});
